import Fuse, { type FuseOptionKey, type RangeTuple } from 'fuse.js';
import type {
  TravellerSearchCardDTO,
  TravellerWithCardsDTO,
} from '@/api/trafikportalen';
import { normalizeCardSerial, toCardSearchQuery } from '@/helpers/cardSerial';
import { formatCardNumber } from '@/helpers/formatting';

export type TravellerSearchCardKind =
  | 'travelcard'
  | 'walletcard'
  | 'schoolcard';

export interface TravellerSearchCardItem extends TravellerSearchCardDTO {
  kind: TravellerSearchCardKind;
}

const toSearchCards = (
  cards: TravellerSearchCardDTO[],
  kind: TravellerSearchCardKind,
): TravellerSearchCardItem[] => cards.map((card) => ({ ...card, kind }));

export const getTravellerSearchCards = (
  result: TravellerWithCardsDTO,
): TravellerSearchCardItem[] => [
  ...toSearchCards(result.travelcards, 'travelcard'),
  ...toSearchCards(result.walletcards, 'walletcard'),
  ...toSearchCards(result.schoolcards, 'schoolcard'),
];

export const getTravellerSearchCardSerials = (
  result: TravellerWithCardsDTO,
): string[] => getTravellerSearchCards(result).map((card) => card.card_serial);

/**
 * Card type for an exact serial in traveller-search results. A serial listed
 * as a school card wins over any other listing. Search matches can be partial,
 * so only the exact serial is classified.
 */
export const getTravellerSearchCardKind = (
  results: TravellerWithCardsDTO[],
  serial: string,
): TravellerSearchCardKind | undefined => {
  const normalized = normalizeCardSerial(serial);
  if (!normalized) return undefined;

  let found: TravellerSearchCardKind | undefined;
  for (const result of results) {
    for (const card of getTravellerSearchCards(result)) {
      if (normalizeCardSerial(card.card_serial) !== normalized) continue;
      if (card.kind === 'schoolcard') return 'schoolcard';
      found ??= card.kind;
    }
  }
  return found;
};

/** Normalized serials that traveller search lists as school cards. */
export const getSchoolCardSerials = (
  results: TravellerWithCardsDTO[],
): string[] => {
  const serials = new Set<string>();
  for (const result of results) {
    for (const card of result.schoolcards) {
      const serial = normalizeCardSerial(card.card_serial);
      if (serial) serials.add(serial);
    }
  }
  return [...serials].sort();
};

export const getMatchingTravellerSearchCards = (
  result: TravellerWithCardsDTO,
  query: string,
): TravellerSearchCardItem[] => {
  const cardQuery = toCardSearchQuery(query);
  if (!cardQuery) {
    return [];
  }

  return getTravellerSearchCards(result).filter((card) =>
    normalizeCardSerial(card.card_serial).includes(cardQuery),
  );
};

export interface SearchCardPurgeOption {
  /** Space-stripped serial sent to the purge endpoint. */
  serial: string;
  /** Serial grouped in fours, for display. */
  displaySerial: string;
  kind: TravellerSearchCardKind;
  /** Only a serial listed solely as a travel card can be purged. */
  purgeable: boolean;
  /** Listed only as a school card, so it can be omitted when students are disabled. */
  schoolOnly: boolean;
}

const purgeOptionKind = (
  kinds: Set<TravellerSearchCardKind>,
): TravellerSearchCardKind => {
  if (kinds.has('walletcard')) return 'walletcard';
  if (kinds.has('schoolcard')) return 'schoolcard';
  return 'travelcard';
};

/**
 * Unique card serials matched by a traveller search. Only travel cards can be
 * purged. School classification wins over travel cards, matching
 * {@link getTravellerSearchCardKind}.
 */
export const getSearchCardPurgeOptions = (
  results: TravellerWithCardsDTO[],
  query: string,
): SearchCardPurgeOption[] => {
  const bySerial = new Map<
    string,
    { serial: string; kinds: Set<TravellerSearchCardKind> }
  >();

  for (const result of results) {
    for (const card of getMatchingTravellerSearchCards(result, query)) {
      const serial = normalizeCardSerial(card.card_serial);
      if (!serial) continue;

      const existing = bySerial.get(serial);
      if (existing) {
        existing.kinds.add(card.kind);
      } else {
        bySerial.set(serial, { serial, kinds: new Set([card.kind]) });
      }
    }
  }

  return [...bySerial.values()]
    .map(({ serial, kinds }) => {
      const kind = purgeOptionKind(kinds);
      return {
        serial,
        displaySerial: formatCardNumber(serial),
        kind,
        purgeable: kind === 'travelcard',
        schoolOnly: kinds.size === 1 && kinds.has('schoolcard'),
      };
    })
    .sort((a, b) => a.serial.localeCompare(b.serial));
};

export interface TravellerResultGroup {
  /** Stable key derived from the grouped traveller record ids. */
  key: string;
  /**
   * Email and national identity number, but only when every record in the
   * group shares the same value. A person can have a different email or NIN per
   * organization; the differing field is left unset here and shown per
   * organization on the individual {@link records} instead.
   */
  email?: string;
  nationalIdentityNumber?: string;
  /** One record per organization the traveller exists in. */
  records: TravellerWithCardsDTO[];
}

export const getFullName = (result: TravellerWithCardsDTO): string | null => {
  const { first_name, last_name } = result.traveller;
  return first_name || last_name
    ? `${first_name ?? ''}${last_name ? ` ${last_name}` : ''}`.trim()
    : null;
};

/**
 * Returns the shared value when every record carries the same non-empty value
 * (compared via {@link normalize}), otherwise `undefined`. Used to decide
 * whether an email or national identity number is common to all of a person's
 * organizations and can be shown once, or differs and must be shown per
 * organization.
 */
const getSharedValue = (
  values: (string | undefined)[],
  normalize: (value: string) => string,
): string | undefined => {
  const first = values[0];
  if (!first) return undefined;
  const normalizedFirst = normalize(first);
  if (!normalizedFirst) return undefined;
  const allShareValue = values.every(
    (value) => value !== undefined && normalize(value) === normalizedFirst,
  );
  return allShareValue ? first : undefined;
};

/**
 * Groups traveller search results that represent the same person. Two records
 * are considered the same person when they share an email or a national
 * identity number; matches are merged transitively (union-find) so a chain of
 * shared identifiers collapses into a single group. Group order follows the
 * first occurrence of each person in the input.
 */
export const groupTravellerSearchResults = (
  results: TravellerWithCardsDTO[],
): TravellerResultGroup[] => {
  const parent = results.map((_, index) => index);

  const find = (index: number): number => {
    let root = index;
    while (parent[root] !== root) {
      root = parent[root];
    }
    let current = index;
    while (parent[current] !== root) {
      const next = parent[current];
      parent[current] = root;
      current = next;
    }
    return root;
  };

  const union = (a: number, b: number) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) {
      parent[rootB] = rootA;
    }
  };

  const firstByEmail = new Map<string, number>();
  const firstByNin = new Map<string, number>();

  results.forEach((result, index) => {
    const email = result.traveller.email?.trim().toLowerCase();
    if (email) {
      const existing = firstByEmail.get(email);
      if (existing === undefined) {
        firstByEmail.set(email, index);
      } else {
        union(existing, index);
      }
    }

    const nin = result.traveller.national_identity_number?.trim();
    if (nin) {
      const existing = firstByNin.get(nin);
      if (existing === undefined) {
        firstByNin.set(nin, index);
      } else {
        union(existing, index);
      }
    }
  });

  const recordsByRoot = new Map<number, TravellerWithCardsDTO[]>();
  const orderedRoots: number[] = [];

  results.forEach((result, index) => {
    const root = find(index);
    let records = recordsByRoot.get(root);
    if (!records) {
      records = [];
      recordsByRoot.set(root, records);
      orderedRoots.push(root);
    }
    records.push(result);
  });

  return orderedRoots.map((root) => {
    const records = recordsByRoot.get(root) ?? [];

    return {
      key: records.map((record) => record.traveller.id).join('|'),
      email: getSharedValue(
        records.map((record) => record.traveller.email),
        (value) => value.trim().toLowerCase(),
      ),
      nationalIdentityNumber: getSharedValue(
        records.map((record) => record.traveller.national_identity_number),
        (value) => value.trim(),
      ),
      records,
    };
  });
};

/** Inclusive `[start, end]` character ranges that matched the search query. */
export type MatchRanges = readonly RangeTuple[];

/**
 * Per-field highlight ranges for the traveller search results. Each map is
 * keyed by the displayed string value (e.g. the full name, an email, a single
 * card serial) so both single-organization results and grouped sysadmin
 * results can look up the ranges for whatever they actually render.
 */
export interface TravellerSearchHighlights {
  fullName: Map<string, MatchRanges>;
  email: Map<string, MatchRanges>;
  bobcatPrivateEmail: Map<string, MatchRanges>;
  nationalIdentityNumber: Map<string, MatchRanges>;
  phone: Map<string, MatchRanges>;
  cards: Map<string, MatchRanges>;
}

type HighlightField = keyof TravellerSearchHighlights;

const HIGHLIGHT_FUSE_KEYS: FuseOptionKey<TravellerWithCardsDTO>[] = [
  { name: 'fullName', getFn: (result) => getFullName(result) ?? '' },
  { name: 'email', getFn: (result) => result.traveller.email ?? '' },
  {
    name: 'bobcatPrivateEmail',
    getFn: (result) => result.traveller.bobcat_private_email ?? '',
  },
  {
    name: 'nationalIdentityNumber',
    getFn: (result) => result.traveller.national_identity_number ?? '',
  },
  { name: 'phone', getFn: (result) => result.traveller.phone ?? '' },
];

const MIN_MATCH_LENGTH = 2;

/**
 * Lower-cases and strips diacritics while keeping a 1:1 character mapping, so
 * indices computed against the folded string stay valid for the original.
 */
const foldForMatch = (value: string): string => {
  let folded = '';
  for (let index = 0; index < value.length; index++) {
    folded += value[index].normalize('NFD').charAt(0).toLowerCase();
  }
  return folded;
};

/** All inclusive `[start, end]` ranges where `needle` occurs in `haystack`. */
const findOccurrences = (haystack: string, needle: string): RangeTuple[] => {
  if (needle.length === 0) {
    return [];
  }
  const ranges: RangeTuple[] = [];
  let from = 0;
  for (;;) {
    const index = haystack.indexOf(needle, from);
    if (index === -1) {
      break;
    }
    ranges.push([index, index + needle.length - 1]);
    from = index + needle.length;
  }
  return ranges;
};

const findExactQueryRanges = (value: string, query: string): MatchRanges =>
  findOccurrences(foldForMatch(value), foldForMatch(query));

/**
 * Inclusive `[start, end]` ranges of the displayed serial that correspond to
 * `cardQuery` in the space-stripped digits. Mapping back through whitespace
 * keeps highlights correct if a serial is shown with grouping spaces.
 */
const findCardSerialMatchRanges = (
  serial: string,
  cardQuery: string,
): RangeTuple[] => {
  if (cardQuery.length === 0) {
    return [];
  }

  const originalIndexByNormalized: number[] = [];
  let normalized = '';
  for (let index = 0; index < serial.length; index++) {
    if (/\s/.test(serial[index] ?? '')) {
      continue;
    }
    originalIndexByNormalized.push(index);
    normalized += serial[index];
  }

  const ranges: RangeTuple[] = [];
  let from = 0;
  for (;;) {
    const matchIndex = normalized.indexOf(cardQuery, from);
    if (matchIndex === -1) {
      break;
    }
    const start = originalIndexByNormalized[matchIndex];
    const end = originalIndexByNormalized[matchIndex + cardQuery.length - 1];
    if (start !== undefined && end !== undefined) {
      ranges.push([start, end]);
    }
    from = matchIndex + cardQuery.length;
  }
  return ranges;
};

export const buildTravellerSearchHighlights = (
  results: TravellerWithCardsDTO[],
  query: string,
): TravellerSearchHighlights => {
  const highlights: TravellerSearchHighlights = {
    fullName: new Map(),
    email: new Map(),
    bobcatPrivateEmail: new Map(),
    nationalIdentityNumber: new Map(),
    phone: new Map(),
    cards: new Map(),
  };

  const trimmedQuery = query.trim();
  if (!trimmedQuery || results.length === 0) {
    return highlights;
  }

  const fuse = new Fuse(results, {
    keys: HIGHLIGHT_FUSE_KEYS,
    includeMatches: true,
    ignoreLocation: true,
    ignoreDiacritics: true,
    threshold: 0.3,
    minMatchCharLength: MIN_MATCH_LENGTH,
  });

  fuse.search(trimmedQuery).forEach((result) => {
    result.matches?.forEach((match) => {
      const { key, value } = match;
      if (!key || !value) {
        return;
      }
      const ranges = highlights[key as HighlightField];
      // Identical values yield identical matches for the same query, so the
      // first occurrence is representative; keep it and skip duplicates.
      if (!ranges || ranges.has(value)) {
        return;
      }
      const exactRanges = findExactQueryRanges(value, trimmedQuery);
      if (exactRanges.length > 0) {
        ranges.set(value, exactRanges);
      }
    });
  });

  const cardQuery = toCardSearchQuery(trimmedQuery);
  if (cardQuery) {
    results.forEach((result) => {
      getTravellerSearchCards(result).forEach((card) => {
        if (highlights.cards.has(card.card_serial)) {
          return;
        }
        const ranges = findCardSerialMatchRanges(card.card_serial, cardQuery);
        if (ranges.length > 0) {
          highlights.cards.set(card.card_serial, ranges);
        }
      });
    });
  }

  return highlights;
};
